import {
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CoreService } from '@app/@core/services/core.service';
import { I18nService } from '@app/i18n';
import { LanguageService } from '@app/@core/services/language.service';
import { TranslateService } from '@ngx-translate/core';
import { ContactService } from '@app/contact/contact.service';
import { ContactInfo } from '@app/contact/contact.types';
import { finalize, take } from 'rxjs/operators';
import { interval, Subscription } from 'rxjs';
import { AuthenticationService } from '../authentication.service';
import { emailRegex } from '@app/@core/services/auth.service';
import { SendOtpResponse } from '@app/@core/models/otp.model';

@Component({
  selector: 'app-reactivate-account',
  templateUrl: './reactivate-account.component.html',
  styleUrls: ['./reactivate-account.component.scss'],
  standalone: false,
})
export class ReactivateAccountComponent implements OnInit, OnDestroy {
  step = 1; // 1 = enter email, 2 = enter OTP
  emailForm: FormGroup;
  otpControl = new FormControl('');
  otpConfig = { length: 6, allowNumbersOnly: true };
  loading = { sending: false, verifying: false };
  isTimerActive = false;
  timer: number;
  timerSubscription: Subscription;
  dir: string;
  currentLanguage: string;
  contactInfo: ContactInfo;

  constructor(
    private authService: AuthenticationService,
    private coreService: CoreService,
    private router: Router,
    private route: ActivatedRoute,
    private i18nService: I18nService,
    private languageService: LanguageService,
    private translateService: TranslateService,
    private contactService: ContactService,
  ) {
    this.currentLanguage = this.i18nService.language;
  }

  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.dir = localStorage.getItem('html-dir');
    this.emailForm = new FormGroup({
      email: new FormControl('', [
        Validators.required,
        Validators.pattern(emailRegex),
      ]),
    });
    const emailParam = this.route.snapshot.queryParamMap.get('email');
    if (emailParam) {
      this.emailForm.get('email').setValue(emailParam);
    }
    this.contactService
      .getContactsInfo()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: ContactInfo) => (this.contactInfo = res),
        error: () => {},
      });
  }

  ngOnDestroy(): void {
    this.stopTimer();
  }

  changeLanguage(lang: string) {
    this.i18nService.language = lang;
    this.languageService.language = lang;
    this.languageService.changeHtmlDirection(lang);
    location.reload();
  }

  proceed() {
    if (this.emailForm.invalid) return;
    this.loading.sending = true;
    this.authService
      .reactivateRequest(this.emailForm.get('email').value)
      .pipe(
        finalize(() => (this.loading.sending = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res: SendOtpResponse) => {
          this.step = 2;
          const expiryMs = res?.expiryTime - Date.now();
          const seconds = expiryMs > 0 ? Math.floor(expiryMs / 1000) : 120;
          this.startTimer(seconds);
        },
        error: (err) => this.coreService.showErrorDialog(err),
      });
  }

  verify() {
    const otp = this.otpControl.value;
    if (!otp || otp.length < 6) return;
    this.loading.verifying = true;
    this.authService
      .reactivateConfirm(this.emailForm.get('email').value, otp)
      .pipe(
        finalize(() => (this.loading.verifying = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.coreService.notify(
            this.translateService.instant('account.reactivate.success'),
          );
          this.router.navigate(['/login'], { replaceUrl: true });
        },
        error: (err) => this.coreService.showErrorDialog(err),
      });
  }

  resend() {
    this.stopTimer();
    this.otpControl.reset('');
    this.proceed();
  }

  getTimerText(sec: number): string {
    if (sec > 60) {
      const m = Math.floor(sec / 60);
      const s = sec % 60;
      return `${m} ${this.translateService.instant('minute(s)')} ${this.translateService.instant('and')} ${s} ${this.translateService.instant('second(s)')}`;
    }
    return `${sec} ${this.translateService.instant('second(s)')}`;
  }

  private startTimer(duration: number) {
    this.isTimerActive = true;
    this.timer = duration;
    this.timerSubscription = interval(1000).subscribe(() => {
      this.timer--;
      if (this.timer <= 0) this.stopTimer();
    });
  }

  private stopTimer() {
    this.timerSubscription?.unsubscribe();
    this.isTimerActive = false;
  }
}
